import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Headphones, MapPin, Globe, Users, Play, ArrowRight, Building2, Ticket, ChevronLeft, ChevronRight, Star, Clock, ShoppingBag, Zap, Wifi, WifiOff, Languages, ShieldCheck, Quote } from 'lucide-react';
import { useBrand } from '../contexts/BrandContext';
import { useLanguage, LANGUAGES } from '../contexts/LanguageContext';
import { formatCurrency } from '../lib/utils';
import { GlobalSearch } from '../components/GlobalSearch';
import { SEO, OrganizationSchema } from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

export default function LandingPage() {
    const brand = useBrand();
    const { language, setLanguage } = useLanguage();
    const navigate = useNavigate();
    const [cities, setCities] = useState([]);
    const [listings, setListings] = useState([]);
    const [loadingListings, setLoadingListings] = useState(true);
    const [pageContent, setPageContent] = useState(null);
    const citySliderRef = useRef(null);
    const listingSliderRef = useRef(null);

    const primaryLanguages = ['en', 'fr', 'it', 'es', 'de', 'pt', 'ja', 'zh', 'ko', 'ar'];

    useEffect(() => {
        fetchPublicData();
    }, []);

    const fetchPublicData = async () => {
        try {
            const [citiesRes, listingsRes, pageRes] = await Promise.all([
                fetch(`${API}/api/public/cities`).then(r => r.json()).catch(() => []),
                fetch(`${API}/api/public/listings`).then(r => r.json()).catch(() => []),
                fetch(`${API}/api/public/pages/home`).then(r => r.json()).catch(() => null)
            ]);
            setCities(Array.isArray(citiesRes) ? citiesRes : []);
            setListings(Array.isArray(listingsRes) ? listingsRes : []);
            if (pageRes) setPageContent(pageRes);
        } catch {}
        finally { setLoadingListings(false); }
    };

    const cms = (sectionId, field, fallback = '') => {
        if (!pageContent) return fallback;
        const section = pageContent.sections?.find(s => s.id === sectionId);
        if (!section) return fallback;
        return section.content?.[field] ?? fallback;
    };
    const cmsSection = (sectionId) => pageContent?.sections?.find(s => s.id === sectionId);
    const isSectionEnabled = (sectionId) => {
        if (!pageContent) return true;
        const section = pageContent.sections?.find(s => s.id === sectionId);
        return section ? section.enabled : true;
    };

    const scrollCity = (dir) => {
        if (citySliderRef.current) {
            citySliderRef.current.scrollBy({ left: dir * 300, behavior: 'smooth' });
        }
    };

    // Static fallback cities if DB is empty
    const displayCities = cities.length > 0 ? cities : [
        { id: '1', name: 'Rome', country: 'Italy', image_url: 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=600' },
        { id: '2', name: 'Paris', country: 'France', image_url: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=600' },
        { id: '3', name: 'London', country: 'UK', image_url: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=600' },
        { id: '4', name: 'Barcelona', country: 'Spain', image_url: 'https://images.unsplash.com/photo-1583422409516-2895a77efded?w=600' },
    ];

    const features = [
        { icon: Headphones, title: 'Audio Guides', desc: 'Professional narration for museums, historical sites, and walking tours — online & offline' },
        { icon: Ticket, title: 'Combo Products', desc: 'Bundle skip-the-line tickets with audio guides for a seamless experience' },
        { icon: Users, title: 'For Travel Pros', desc: 'Audio solutions for tour operators, DMCs, OTAs, and travel agents' },
        { icon: Globe, title: 'White Label', desc: 'Custom-branded audio platform with your logo, colors, and domain' },
    ];

    const getLowestPrice = (listing) => {
        const opts = listing.options || [];
        if (opts.length === 0) return 0;
        return Math.min(...opts.map(o => o.pricing?.adult || 0));
    };

    const getImageUrl = (img) => {
        if (!img) return null;
        if (img.startsWith('http')) return img;
        return `${API}${img}`;
    };

    return (
        <div className="min-h-screen bg-[#0A0D14] overflow-x-hidden">
            <SEO
                title="Audio Travel Guides & Skip-the-Line Tickets"
                description="Discover premium self-guided audio tours and skip-the-line attraction tickets for museums, historical sites, and walking tours worldwide. Available in 10+ languages, works offline."
                type="website"
            />
            <OrganizationSchema />
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 glass">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
                    <div className="flex items-center gap-2 sm:gap-3">
                        {brand.logo_url ? (
                            <img src={brand.logo_url} alt={brand.platform_name} className="h-7 sm:h-9 max-w-[120px] sm:max-w-[160px] object-contain" />
                        ) : (
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-md bg-gold flex items-center justify-center">
                                <Headphones className="w-4 h-4 sm:w-5 sm:h-5 text-[#0A0D14]" />
                            </div>
                        )}
                        {!brand.logo_url && <span className="font-outfit font-semibold text-base sm:text-xl text-white">{brand.platform_name}</span>}
                    </div>
                    <div className="flex items-center gap-2 sm:gap-4">
                        <Link to="/access">
                            <Button className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-1.5 sm:gap-2 text-xs sm:text-sm h-9 sm:h-10 px-3 sm:px-4" data-testid="access-nav-btn">
                                <Ticket className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> I Have a Code
                            </Button>
                        </Link>
                        <Link to="/login">
                            <Button variant="ghost" className="text-white/60 hover:text-white text-xs sm:text-sm h-9 sm:h-10 px-2 sm:px-4" data-testid="login-nav-btn">
                                <span className="hidden sm:inline">Staff Login</span>
                                <span className="sm:hidden">Login</span>
                            </Button>
                        </Link>
                    </div>
                </div>
            </header>

            {/* Hero Section */}
            {isSectionEnabled('hero') && <section className="relative pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6">
                <div className="absolute inset-0 bg-gradient-to-b from-gold/5 to-transparent" />
                <div
                    className="absolute inset-0 opacity-20"
                    style={{
                        backgroundImage: `url(${(() => {
                            const img = cms('hero', 'bg_image', '');
                            if (img && img.startsWith('/api')) return `${API}${img}`;
                            return img || 'https://images.unsplash.com/photo-1688233923543-7b972d1b171a?w=1920';
                        })()})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D14] via-[#0A0D14]/80 to-transparent" />

                <div className="relative max-w-4xl mx-auto text-center">
                    <p className="text-gold uppercase tracking-widest text-xs sm:text-sm mb-4 sm:mb-6 animate-fade-in">Premium Audio Experiences</p>
                    <h1 className="font-outfit text-3xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 animate-fade-in stagger-1">
                        Immersive Audio Experiences for
                        <span className="text-gold-gradient block mt-2">World-Class Destinations</span>
                    </h1>
                    <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto mb-6 sm:mb-8 animate-fade-in stagger-2">
                        Self-guided audio tours and skip-the-line ticket bundles for museums, historical sites, and walking tours. For travelers, tour operators, and travel businesses.
                    </p>

                    {/* CTA Buttons */}
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-6 sm:mb-8 animate-fade-in stagger-2">
                        <Link to="/access" className="w-full sm:w-auto">
                            <Button size="lg" className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2 w-full sm:w-auto" data-testid="hero-cta-btn">
                                Enter Your Code <ArrowRight className="w-4 h-4" />
                            </Button>
                        </Link>
                        {listings.length > 0 && (
                            <a href="#attractions" className="w-full sm:w-auto">
                                <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10 gap-2 w-full sm:w-auto" data-testid="browse-btn">
                                    <ShoppingBag className="w-4 h-4" /> Browse Attractions
                                </Button>
                            </a>
                        )}
                    </div>

                    {/* Global Search */}
                    <div className="relative z-30 animate-fade-in stagger-3">
                        <GlobalSearch />
                    </div>
                </div>
            </section>}

            {/* Cities Slider */}
            {isSectionEnabled('cities') && <section className="py-12 sm:py-20 px-4 sm:px-6 border-t border-white/5" data-testid="cities-section">
                <div className="max-w-7xl mx-auto">
                    <div className="flex items-center justify-between mb-8 sm:mb-12">
                        <div>
                            <p className="text-gold uppercase tracking-widest text-xs sm:text-sm mb-2">Destinations</p>
                            <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl font-bold text-white">Explore Cities</h2>
                        </div>
                        <div className="hidden sm:flex gap-2">
                            <button onClick={() => scrollCity(-1)} className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:border-white/30 transition-colors" data-testid="city-scroll-left">
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <button onClick={() => scrollCity(1)} className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:border-white/30 transition-colors" data-testid="city-scroll-right">
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                    <div ref={citySliderRef} className="flex gap-4 sm:gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                        {displayCities.map((city, i) => (
                            <button
                                key={city.id}
                                onClick={() => navigate(`/city/${city.name.toLowerCase().replace(/\s+/g, '-')}`)}
                                className="group relative flex-shrink-0 w-[260px] sm:w-[300px] aspect-[3/4] rounded-2xl overflow-hidden snap-start cursor-pointer"
                                style={{ animationDelay: `${i * 0.08}s` }}
                                data-testid={`city-card-${city.id}`}
                            >
                                <img
                                    src={getImageUrl(city.image_url) || `https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=600`}
                                    alt={city.name}
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D14] via-[#0A0D14]/30 to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
                                    <h3 className="font-outfit font-semibold text-lg sm:text-xl text-white mb-1">{city.name}</h3>
                                    <p className="text-sm text-gold flex items-center gap-1"><MapPin className="w-3 h-3" />{city.country}</p>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            </section>}

            {/* Attractions / Listings */}
            {isSectionEnabled('listings') && listings.length > 0 && (
                <section id="attractions" className="py-12 sm:py-20 px-4 sm:px-6 bg-[#121620]/50" data-testid="attractions-section">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-10 sm:mb-14">
                            <p className="text-gold uppercase tracking-widest text-xs sm:text-sm mb-3">Experiences</p>
                            <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl font-bold text-white">Book Your Experience</h2>
                            <p className="text-muted-foreground mt-3 max-w-lg mx-auto text-sm sm:text-base">Choose audio guides or complete packages with skip-the-line tickets</p>
                        </div>
                        <div className="relative">
                            <div ref={listingSliderRef} className="flex gap-5 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-4" style={{ scrollBehavior: 'smooth', scrollPaddingLeft: '24px' }}>
                                <div className="shrink-0 w-[calc((100%-380px*3-40px)/2)] hidden lg:block" />
                                {listings.map((listing, i) => (
                                    <button
                                        key={listing.id}
                                        onClick={() => navigate(`/experience/${listing.id}`)}
                                        className="group text-left bg-[#0A0D14] border border-white/10 rounded-2xl overflow-hidden transition-all duration-300 hover:border-gold/30 hover:-translate-y-1 flex-shrink-0 snap-start w-[85vw] sm:w-[340px] lg:w-[380px]"
                                        style={{ animationDelay: `${i * 0.08}s` }}
                                        data-testid={`listing-card-${listing.id}`}
                                    >
                                    <div className="relative aspect-[16/10] overflow-hidden">
                                        {getImageUrl(listing.featured_image) ? (
                                            <img
                                                src={getImageUrl(listing.featured_image)}
                                                alt={listing.title}
                                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                            />
                                        ) : (
                                            <div className="w-full h-full bg-gradient-to-br from-gold/10 to-gold/5 flex items-center justify-center">
                                                <Building2 className="w-12 h-12 text-gold/30" />
                                            </div>
                                        )}
                                        <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
                                            {(listing.options || []).slice(0, 2).map((opt, idx) => {
                                                const isCombo = opt.has_tickets || (opt.pricing?.adult > 0 && idx > 0);
                                                return (
                                                    <span key={opt.id} className={`px-2.5 py-1 rounded-full text-xs font-medium backdrop-blur-sm flex items-center gap-1 ${isCombo ? 'bg-amber-500/90 text-[#0A0D14]' : 'bg-emerald-500/90 text-white'}`}>
                                                        {isCombo ? <Ticket className="w-3 h-3" /> : <Headphones className="w-3 h-3" />} {opt.name.length > 18 ? opt.name.substring(0, 18) + '...' : opt.name}
                                                    </span>
                                                );
                                            })}
                                            {(listing.options || []).length > 2 && <span className="px-2 py-1 rounded-full bg-white/20 text-white text-xs backdrop-blur-sm">+{listing.options.length - 2}</span>}
                                        </div>
                                    </div>
                                    <div className="p-4 sm:p-5">
                                        <div className="flex items-center gap-2 mb-2">
                                            <MapPin className="w-3.5 h-3.5 text-gold" />
                                            <span className="text-xs text-gold">{listing.city_name}</span>
                                            <span className="text-xs text-white/30 ml-auto">{listing.category}</span>
                                        </div>
                                        <h3 className="font-outfit font-semibold text-white text-base sm:text-lg mb-2 group-hover:text-gold transition-colors line-clamp-2">
                                            {listing.title}
                                        </h3>
                                        {listing.instant_confirmation && (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 text-[10px] font-medium border border-emerald-500/20 mb-2" data-testid={`instant-badge-${listing.id}`}>
                                                <Zap className="w-2.5 h-2.5" />Instant Confirmation
                                            </span>
                                        )}
                                        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                                            {listing.short_description || listing.description}
                                        </p>
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-xs text-muted-foreground">From</p>
                                                <p className="text-lg font-outfit font-bold text-white">
                                                    {formatCurrency(getLowestPrice(listing), listing.currency?.toUpperCase() || 'EUR')}
                                                </p>
                                            </div>
                                            <span className="px-4 py-2 rounded-full bg-gold/10 text-gold text-sm font-medium group-hover:bg-gold group-hover:text-[#0A0D14] transition-all">
                                                View Details
                                            </span>
                                        </div>
                                    </div>
                                </button>
                            ))}
                            <div className="shrink-0 w-[calc((100%-380px*3-40px)/2)] hidden lg:block" />
                            </div>
                            {/* Slider Arrows */}
                            <button onClick={() => listingSliderRef.current?.scrollBy({ left: -390, behavior: 'smooth' })}
                                className="hidden sm:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-[#0A0D14] border border-white/10 items-center justify-center text-white/50 hover:text-white hover:border-gold/30 transition-colors shadow-xl" data-testid="listing-slider-prev">
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <button onClick={() => listingSliderRef.current?.scrollBy({ left: 390, behavior: 'smooth' })}
                                className="hidden sm:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-[#0A0D14] border border-white/10 items-center justify-center text-white/50 hover:text-white hover:border-gold/30 transition-colors shadow-xl" data-testid="listing-slider-next">
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </section>
            )}

            {/* Features */}
            {isSectionEnabled('features') && <section className="py-16 sm:py-24 px-4 sm:px-6 border-t border-white/5 relative overflow-hidden">
                {/* Subtle background glow */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gold/[0.03] rounded-full blur-[120px] pointer-events-none" />
                <div className="max-w-7xl mx-auto relative">
                    <div className="text-center mb-10 sm:mb-16">
                        <p className="text-gold uppercase tracking-widest text-xs sm:text-sm mb-3 sm:mb-4">What We Offer</p>
                        <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl font-bold text-white">Everything You Need</h2>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
                        {features.map((feature, i) => (
                            <div
                                key={feature.title}
                                className="group relative p-5 sm:p-7 rounded-2xl bg-gradient-to-b from-white/[0.04] to-transparent border border-white/[0.06] hover:border-gold/20 transition-all duration-500 animate-fade-in"
                                style={{ animationDelay: `${i * 0.1}s` }}
                            >
                                <div className="absolute inset-0 rounded-2xl bg-gradient-to-b from-gold/[0.03] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                <div className="relative">
                                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gold/10 group-hover:bg-gold/15 flex items-center justify-center mb-5 transition-colors duration-300 ring-1 ring-gold/10 group-hover:ring-gold/25">
                                        <feature.icon className="w-5 h-5 sm:w-6 sm:h-6 text-gold group-hover:scale-110 transition-transform duration-300" />
                                    </div>
                                    <h3 className="font-outfit font-semibold text-white mb-2 text-base sm:text-lg">{feature.title}</h3>
                                    <p className="text-sm text-white/40 leading-relaxed">{feature.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>}

            {/* How It Works */}
            {isSectionEnabled('how_it_works') && <section className="py-16 sm:py-24 px-4 sm:px-6 border-t border-white/5">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-12 sm:mb-16">
                        <p className="text-gold uppercase tracking-widest text-xs sm:text-sm mb-3">{cms('how_it_works', 'section_label', 'Simple Process')}</p>
                        <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl font-bold text-white">{cms('how_it_works', 'title', 'How It Works')}</h2>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-6 sm:gap-8 relative">
                        {/* Connector line */}
                        <div className="hidden sm:block absolute top-16 left-[20%] right-[20%] h-[1px] bg-gradient-to-r from-transparent via-gold/20 to-transparent" />
                        {[
                            { step: '01', title: 'Choose Your Experience', desc: 'Browse destinations and select audio guides or ticket bundles for your favourite attractions.', icon: MapPin },
                            { step: '02', title: 'Book Instantly', desc: 'Secure your tickets with instant confirmation. Skip-the-line access included with combo packages.', icon: Zap },
                            { step: '03', title: 'Listen & Explore', desc: 'Access your audio guide on any device. Professional narration in multiple languages, available offline.', icon: Headphones },
                        ].map((item, i) => (
                            <div key={item.step} className="relative text-center animate-fade-in" style={{ animationDelay: `${i * 0.15}s` }}>
                                <div className="relative mx-auto w-20 h-20 sm:w-24 sm:h-24 mb-6">
                                    <div className="absolute inset-0 rounded-2xl bg-gold/[0.08] rotate-6" />
                                    <div className="relative w-full h-full rounded-2xl bg-[#121620] border border-white/10 flex items-center justify-center">
                                        <item.icon className="w-8 h-8 sm:w-10 sm:h-10 text-gold" />
                                    </div>
                                    <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-gold text-[#0A0D14] text-xs font-bold flex items-center justify-center">{item.step}</div>
                                </div>
                                <h3 className="font-outfit font-semibold text-white text-base sm:text-lg mb-2">{item.title}</h3>
                                <p className="text-sm text-white/40 leading-relaxed max-w-[280px] mx-auto">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>}

            {/* Stats / Social Proof */}
            {isSectionEnabled('stats') && <section className="py-14 sm:py-20 px-4 sm:px-6 bg-gradient-to-r from-gold/[0.04] via-gold/[0.08] to-gold/[0.04] border-y border-gold/10">
                <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
                        {(cmsSection('stats')?.content?.items || [
                            { value: '50+', label: 'Destinations', suffix: '' },
                            { value: '200+', label: 'Audio Guides', suffix: '' },
                            { value: '10K+', label: 'Happy Travelers', suffix: '' },
                            { value: '4.9', label: 'Average Rating', suffix: '/5' },
                        ]).map((stat, i) => (
                            <div key={stat.label} className="text-center animate-fade-in" style={{ animationDelay: `${i * 0.1}s` }}>
                                <p className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold text-gold mb-1">
                                    {stat.value}<span className="text-gold/50 text-lg sm:text-xl">{stat.suffix}</span>
                                </p>
                                <p className="text-sm text-white/50 tracking-wide">{stat.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>}

            {/* Why Choose Voxoora */}
            {isSectionEnabled('why_choose') && <section className="py-16 sm:py-24 px-4 sm:px-6 border-b border-white/5">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12 sm:mb-16">
                        <p className="text-gold uppercase tracking-widest text-xs sm:text-sm mb-3">{cms('why_choose', 'section_label', `The ${brand.platform_name} Advantage`)}</p>
                        <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl font-bold text-white">{cms('why_choose', 'title', 'Why Travelers Choose Us')}</h2>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {[
                            { icon: Zap, title: 'Skip the Queue', desc: 'Skip-the-line tickets bundled with audio guides. Walk straight in.', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/15' },
                            { icon: Languages, title: 'Multi-Language', desc: '10+ languages. Listen in your native language for deeper understanding.', color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/15' },
                            { icon: WifiOff, title: 'Works Offline', desc: 'Download before you go. No internet needed at the attraction.', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/15' },
                            { icon: ShieldCheck, title: 'Expert Narration', desc: 'Professional guides and historians craft every word. Museum-quality audio.', color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/15' },
                        ].map((item, i) => (
                            <div key={item.title} className={`p-5 sm:p-6 rounded-2xl border ${item.bg} animate-fade-in`} style={{ animationDelay: `${i * 0.1}s` }}>
                                <item.icon className={`w-8 h-8 ${item.color} mb-4`} />
                                <h3 className="font-outfit font-semibold text-white mb-2">{item.title}</h3>
                                <p className="text-sm text-white/40 leading-relaxed">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>}

            {/* Testimonials */}
            {isSectionEnabled('testimonials') && <section className="py-16 sm:py-24 px-4 sm:px-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-gold/[0.02] rounded-full blur-[100px] pointer-events-none" />
                <div className="max-w-6xl mx-auto relative">
                    <div className="text-center mb-12 sm:mb-16">
                        <p className="text-gold uppercase tracking-widest text-xs sm:text-sm mb-3">{cms('testimonials', 'section_label', 'Testimonials')}</p>
                        <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl font-bold text-white">{cms('testimonials', 'title', 'What Travelers Say')}</h2>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {(cmsSection('testimonials')?.content?.reviews || [
                            { name: 'Sarah M.', location: 'New York, USA', text: 'The Colosseum audio guide was incredible!', rating: 5 },
                            { name: 'Luca B.', location: 'Berlin, Germany', text: 'Skip-the-line ticket saved us 2 hours!', rating: 5 },
                            { name: 'Yuki T.', location: 'Tokyo, Japan', text: 'Worked flawlessly offline!', rating: 5 },
                        ]).map((review, i) => (
                            <div key={review.name} className="relative p-6 rounded-2xl bg-gradient-to-b from-white/[0.04] to-transparent border border-white/[0.06] animate-fade-in" style={{ animationDelay: `${i * 0.12}s` }}>
                                <Quote className="w-8 h-8 text-gold/15 absolute top-5 right-5" />
                                <div className="flex items-center gap-1 mb-4">
                                    {Array.from({ length: review.rating }).map((_, j) => (
                                        <Star key={j} className="w-4 h-4 text-gold fill-gold" />
                                    ))}
                                </div>
                                <p className="text-sm text-white/60 leading-relaxed mb-5">"{review.text}"</p>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-gold/15 flex items-center justify-center text-gold font-semibold text-sm">
                                        {review.name.charAt(0)}
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-white">{review.name}</p>
                                        <p className="text-xs text-white/30">{review.location}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>}

            {/* Partner CTA */}
            {isSectionEnabled('partner_cta') && <section className="py-16 sm:py-24 px-4 sm:px-6">
                <div className="max-w-3xl mx-auto text-center">
                    <p className="text-gold uppercase tracking-widest text-xs sm:text-sm mb-3 sm:mb-4">For Businesses</p>
                    <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">{cms('partner_cta', 'title', 'Become a Partner')}</h2>
                    <p className="text-muted-foreground text-sm sm:text-base max-w-xl mx-auto mb-8">
                        {cms('partner_cta', 'subtitle', 'Tour operators, DMCs, OTAs, hotels, and museums — offer immersive audio experiences to your clients with white-label branding and multi-language support.')}
                    </p>
                    <Link to={cms('partner_cta', 'button_link', '/register/operator')}>
                        <Button size="lg" className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2" data-testid="partner-cta-btn">
                            <Building2 className="w-4 h-4" /> {cms('partner_cta', 'button_text', 'Get Started as a Partner')} <ArrowRight className="w-4 h-4" />
                        </Button>
                    </Link>
                </div>
            </section>}

            {/* Footer */}
            <footer className="py-8 sm:py-12 px-4 sm:px-6 border-t border-white/10">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-6 mb-6">
                        <div className="flex items-center gap-2 sm:gap-3">
                            {brand.logo_url ? (
                                <img src={brand.logo_url} alt={brand.platform_name} className="h-6 sm:h-8 max-w-[100px] sm:max-w-[140px] object-contain" />
                            ) : (
                                <div className="w-8 h-8 rounded-md bg-gold flex items-center justify-center">
                                    <Headphones className="w-4 h-4 text-[#0A0D14]" />
                                </div>
                            )}
                            {!brand.logo_url && <span className="font-outfit font-semibold text-white">{brand.platform_name}</span>}
                        </div>
                        <div className="flex items-center gap-5 sm:gap-6">
                            <Link to="/contact" className="text-xs sm:text-sm text-white/50 hover:text-white transition-colors">Contact</Link>
                            <Link to="/blog" className="text-xs sm:text-sm text-white/50 hover:text-white transition-colors">Travel Insider</Link>
                            <Link to="/terms" className="text-xs sm:text-sm text-white/50 hover:text-white transition-colors">Terms & Conditions</Link>
                            <Link to="/privacy" className="text-xs sm:text-sm text-white/50 hover:text-white transition-colors">Privacy Policy</Link>
                        </div>
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground text-center">
                        {brand.footer_text || `\u00A9 2026 ${brand.platform_name}. All rights reserved.`}
                    </p>
                </div>
            </footer>
        </div>
    );
}
